'use client'

// ============================================================================
// МЕНЮ ПОД АВАТАРОМ В ШАПКЕ КАТАЛОГА.
// ----------------------------------------------------------------------------
// Каталог — единственное место продукта, где человек видит ЧУЖИЕ фирмы. Отсюда
// два вопроса, которых нет на других экранах: «под кем я сейчас» и «как отсюда
// вернуться к своей работе». На оба отвечает это меню.
//
// ── ПЯТЬ ПУНКТОВ, А НЕ ВСЁ МЕНЮ КАБИНЕТА ───────────────────────────────────
// В боковом меню больше сорока пунктов в восьми группах. Копия здесь была бы
// списком, по которому надо скроллить, — то есть не помощью, а поиском.
// Оставлены те, куда идут ИЗ каталога: посмотрел фирму — заводит сделку или
// проверяет, есть ли она в партнёрах.
//
// ── ОТКРЫВАЕТСЯ НАЖАТИЕМ ───────────────────────────────────────────────────
// Не наведением: на телефоне наведения не существует, и меню было бы
// недоступно половине посетителей. Делать по-разному на узком и широком экране
// значит завести два поведения у одного места.
//
// ── СЧЁТЧИКОВ ЗДЕСЬ НЕТ ────────────────────────────────────────────────────
// Решение владельца. Они есть в боковом меню кабинета, и второе место, где их
// надо держать в согласии, породило бы расхождение при первой же правке.
// ============================================================================

import { useEffect, useRef, useState } from 'react'
import Link from 'next/link'
import { useRouter } from 'next/navigation'
// 903: подписи — из словаря; пункты кабинета — те же ключи, что в боковом меню.
import { useTranslations } from 'next-intl'
import { createClient } from '@/lib/supabase/client'

export interface AvatarMenuUser {
  initials: string
  fullName: string
  email: string | null
  companyName: string | null
  isOwner: boolean
  /** Есть ли у человека кабинет. Нет — он зарегистрировался ради вопросов. */
  hasCompany: boolean
}

/** Куда ходят из каталога. label — ключ словаря: те же, что в боковом меню кабинета. */
const ITEMS = [
  { href: '/dashboard', label: 'nav.sectionWorkspace' },
  { href: '/deals', label: 'nav.deals' },
  { href: '/tenders', label: 'nav.tenders' },
  { href: '/partners', label: 'nav.partners' },
  { href: '/tasks', label: 'catalog.menu.myDay' },
]

/** Видно только владельцу — как и в боковом меню. */
const OWNER_ITEMS = [
  { href: '/hr', label: 'nav.hr' },
  { href: '/settings/billing', label: 'nav.billing' },
]

export function AvatarMenu({ user }: { user: AvatarMenuUser }) {
  const t = useTranslations()
  const [open, setOpen] = useState(false)
  const [pending, setPending] = useState(false)
  const box = useRef<HTMLDivElement>(null)
  const router = useRouter()

  // Нажатие мимо и Escape закрывают меню. Без этого оно висит поверх списка
  // компаний, а список тут главное.
  useEffect(() => {
    if (!open) return
    const onClick = (e: MouseEvent) => {
      if (!box.current?.contains(e.target as Node)) setOpen(false)
    }
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false) }
    document.addEventListener('mousedown', onClick)
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('mousedown', onClick)
      document.removeEventListener('keydown', onKey)
    }
  }, [open])

  const logout = async () => {
    setPending(true)
    try {
      await createClient().auth.signOut()
      // Остаёмся на той же странице: человек читал карточку компании и
      // продолжает её читать, просто теперь как гость.
      router.refresh()
      setOpen(false)
    } finally {
      setPending(false)
    }
  }

  return (
    <div ref={box} className="relative">
      <button
        type="button"
        onClick={() => setOpen(v => !v)}
        aria-expanded={open}
        aria-haspopup="menu"
        className="flex items-center gap-2.5"
      >
        <span className="hidden text-right leading-tight sm:block">
          <span className="block text-[13px] font-semibold text-ink-900 dark:text-white">{user.fullName}</span>
          {user.companyName ? (
            <span className="block text-[11px] uppercase tracking-wide text-ink-400 dark:text-ink-500">
              {user.companyName}
            </span>
          ) : null}
        </span>
        <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-ink-900 text-[11.5px] font-bold text-white">
          {user.initials}
        </span>
      </button>

      {open ? (
        <div
          role="menu"
          className="absolute right-0 top-11 z-50 w-[268px] rounded-[13px] border-hairline border-ink-200 dark:border-ink-700 bg-white dark:bg-ink-800 p-1.5 shadow-lg"
        >
          <div className="border-b border-ink-150 dark:border-ink-700 px-3 pb-2.5 pt-2">
            <div className="text-[13.5px] font-semibold text-ink-900 dark:text-white">{user.fullName}</div>
            {user.email ? (
              <div className="mt-0.5 text-[12px] text-ink-500 dark:text-ink-400">{user.email}</div>
            ) : null}
            {user.companyName ? (
              <div className="mt-1.5 inline-block rounded bg-status-green-bg px-2 py-0.5 text-[10.5px] font-bold uppercase tracking-wide text-status-green-text">
                {user.isOwner ? t('catalog.menu.ownerPrefix') : ''}{user.companyName}
              </div>
            ) : null}
          </div>

          {user.hasCompany ? (
            <div className="mt-1">
              {ITEMS.map(i => <Item key={i.href} href={i.href} label={t(i.label)} onGo={() => setOpen(false)} />)}

              {user.isOwner ? (
                <>
                  <Sep />
                  {OWNER_ITEMS.map(i => <Item key={i.href} href={i.href} label={t(i.label)} onGo={() => setOpen(false)} />)}
                </>
              ) : null}
            </div>
          ) : (
            // ── ПЕРЕВОЗЧИК БЕЗ КАБИНЕТА ──────────────────────────────────
            // Он зарегистрировался ради вопросов, и вести его в «Сделки»
            // некуда — там пусто. Вместо пустого меню приглашение, где сказано
            // ЧТО он получит, а не «попробуйте бесплатно».
            <div className="m-1 rounded-xl bg-status-green-bg dark:bg-status-green-bg/10 p-3">
              <div className="text-[13px] font-bold text-ink-900 dark:text-white">
                {t('catalog.menu.inviteTitle')}
              </div>
              <ul className="mt-1.5 space-y-0.5 text-[12px] text-ink-700 dark:text-ink-300">
                <li>· {t('catalog.menu.invitePage')}</li>
                <li>· {t('catalog.menu.inviteCrm')}</li>
                <li>· {t('catalog.menu.inviteCargo')}</li>
              </ul>
              <Link
                href="/onboarding"
                onClick={() => setOpen(false)}
                className="mt-2.5 block rounded-lg bg-ink-900 px-3 py-2 text-center text-[12.5px] font-bold text-white"
              >
                {t('catalog.menu.inviteButton')}
              </Link>
            </div>
          )}

          <Sep />
          <Item href="/settings" label={t('nav.settings')} onGo={() => setOpen(false)} />

          <Sep />
          <button
            type="button" disabled={pending} onClick={logout}
            className="w-full rounded-[9px] px-3 py-2 text-left text-[13.5px] font-medium text-status-red-text hover:bg-ink-50 dark:hover:bg-ink-900 disabled:opacity-50"
          >
            {pending ? t('catalog.menu.signingOut') : t('catalog.menu.signOut')}
          </button>
        </div>
      ) : null}
    </div>
  )
}

function Item({ href, label, onGo }: { href: string; label: string; onGo: () => void }) {
  return (
    <Link
      href={href}
      onClick={onGo}
      role="menuitem"
      className="block rounded-[9px] px-3 py-2 text-[13.5px] text-ink-700 dark:text-ink-300 hover:bg-ink-50 dark:hover:bg-ink-900 hover:text-ink-900 dark:hover:text-white"
    >
      {label}
    </Link>
  )
}

function Sep() {
  return <div className="my-1.5 h-px bg-ink-150 dark:bg-ink-700" />
}
